import type { Draft } from 'immer';
import { produce } from 'immer';
import type { ReactNode } from 'react';

import { zustandStoreFactory } from './zustandStoreFactory';

import type { StateCreator } from '@shared-types/zustand.types';

type State<T extends object> = {
  options: T;
  setOptions: (newOptions: T | ((state: Draft<T>) => T)) => void;
};

export const getOptionsProvider = <T extends object>(name?: string) => {
  const optionsSlice: StateCreator<State<T>> = (set) => ({
    options: {} as T,
    setOptions: (newOptions: T | ((state: Draft<T>) => T)) =>
      set((state) => ({
        options: typeof newOptions === 'function' ? produce<T>(newOptions)(state.options) : newOptions,
      })),
  });

  const { Provider, useStore: useOptionsStore } = zustandStoreFactory<State<T>>(optionsSlice, name);

  const useOptions = () => {
    const options = useOptionsStore((state) => state.options);
    const setOptions = useOptionsStore((state) => state.setOptions);
    return { options, setOptions };
  };

  const OptionsProvider = ({ children, options }: { children: ReactNode; options?: T }) => (
    <Provider state={{ options }}>{children}</Provider>
  );

  return { OptionsProvider, useOptions };
};
